// src/components/preview/resolution/ResolutionPreview.jsx
// ✅ 결의서(WEHAGO 양식) 미리보기 — 지출결의/수입결의 공용 구현. 공문형(수신/경유/
// 붙임) 레이아웃이 아니라 제목 + 우상단 결재란 + 필드-값 2열 테이블로 구성된 단일
// 문서(세트 없음). kind prop("expense"|"income")으로 라벨만 갈아끼우고 구조는
// 완전히 공유한다 — ExpenseResolutionPreview.jsx/IncomeResolutionPreview.jsx는
// 이 컴포넌트를 감싸는 얇은 래퍼.
// A4 용지/인쇄 규격은 IncomeProposalPreview.css를 그대로 재사용(기존 print CSS 패턴).
import React from "react";
import html2pdf from "html2pdf.js";
import "../income/IncomeProposalPreview.css";
import "./ResolutionPreview.css";

const PAY_METHOD_LABEL = {
  transfer: "계좌이체",
  card: "카드결제",
  cash: "현금결제",
};

const LABELS = {
  expense: {
    docTitle: "지출결의서",
    dateLabel: "지출일",
    amountLabel: "지출금액",
    methodLabel: "지출방법",
    accountLabel: "계좌정보",
    budgetLabel: "지출과목",
    defaultFilename: "지출결의서",
  },
  income: {
    docTitle: "수입결의서",
    dateLabel: "입금일",
    amountLabel: "수입금액",
    methodLabel: "입금방법",
    accountLabel: "입금계좌",
    budgetLabel: "수입과목",
    defaultFilename: "수입결의서",
  },
};

// ✅ 저장된 DraftDocument(document_type='expense_resolution'|'income_resolution')
// 하나를 이 컴포넌트의 props로 변환 — 저장 시 template_data에 스냅샷해둔 값(내부
// 기안번호/계좌정보/예산·수입과목 등)을 그대로 읽는다(재계산 없음). 상세
// (DraftDocumentDetail.jsx)·수신결재/결재진행/결재완료(ApprovalInbox.jsx,
// ApprovalInProgress.jsx) 등 결의서를 열람하는 모든 화면이 이 함수를 공유한다.
// approval_lines(DraftDocumentDetailSerializer, approver_name)와 all_approval_lines
// (ApprovalInbox/ApprovalInProgress의 커스텀 응답, approver__name) 두 형태를 모두
// 받는다. 거래일 키는 지출결의(expenseDate)/수입결의(depositDate) 저장 시점에
// 각자 다른 이름으로 template_data에 쌓이므로 둘 다 확인한다.
export function buildResolutionProps(doc) {
  const td = doc.template_data || {};
  const rawLines = doc.approval_lines || doc.all_approval_lines || [];
  return {
    categoryLabel: td.categoryLabel || doc.category || "",
    documentTitle: doc.title || "",
    draftDate: doc.draft_date || "",
    drafterLine1: td.drafterLine1 || "",
    drafterLine2: td.drafterLine2 || "",
    transactionDate: td.expenseDate || td.depositDate || td.transactionDate || "",
    partnerName: doc.partner_name || "",
    amount: doc.amount || "",
    methodLabel: PAY_METHOD_LABEL[doc.pay_method] || doc.pay_method || "",
    accountInfo: td.accountInfo || "",
    budgetItemFormatted: td.budgetItemFormatted || "",
    internalDraftNo: td.internalDraftNo || "",
    attachmentsText: td.attachmentsText || "",
    approvalLine: rawLines.map((l) => ({
      id: l.id,
      position: l.position || "",
      name: l.approver_name || l.approver__name || l.name || "",
      approvalType: l.approval_type || "",
      result: l.result,
      approved_at: l.approved_at,
    })),
  };
}

export default function ResolutionPreview({
  kind = "expense",
  categoryLabel,
  documentTitle,
  draftDate,
  drafterLine1,
  drafterLine2,
  transactionDate,
  partnerName,
  amount,
  methodLabel,
  accountInfo,
  budgetItemFormatted,
  internalDraftNo,
  attachmentsText,
  approvalLine = [],
  onSave,
  saving = false,
}) {
  const L = LABELS[kind] || LABELS.expense;

  const withPaper = (run) => {
    const el = document.querySelector(".preview-paper");
    if (!el) return;
    const original = el.style.height;
    el.style.height = "297mm";
    el.style.minHeight = "297mm";
    el.style.maxHeight = "297mm";
    const opt = {
      margin: 0,
      filename: `${documentTitle || L.defaultFilename}_${new Date().toISOString().slice(0, 10)}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, logging: false, backgroundColor: "#ffffff", width: 794, height: 1123 },
      jsPDF: { unit: "px", format: [794, 1123], orientation: "portrait", hotfixes: ["px_scaling"] },
    };
    run(html2pdf().set(opt).from(el))
      .then(() => (el.style.height = original))
      .catch(() => (el.style.height = original));
  };

  const handlePreviewPDF = () =>
    withPaper((chain) =>
      chain.outputPdf("blob").then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 100);
      })
    );

  const handleDownloadPDF = () => withPaper((chain) => chain.save());

  return (
    <div className="income-preview-wrapper">
      <div className="preview-header">
        <h3 className="preview-title">📄 미리보기</h3>
        <div style={{ display: "flex", gap: "10px" }}>
          {onSave && (
            <button type="button" onClick={onSave} disabled={saving} className="print-button save-button">
              {saving ? "⏳ 저장중..." : "💾 저장"}
            </button>
          )}
          <button type="button" onClick={handlePreviewPDF} className="print-button preview-button">
            👁️ PDF 미리보기
          </button>
          <button type="button" onClick={handleDownloadPDF} className="print-button download-button">
            📥 PDF 다운로드
          </button>
        </div>
      </div>

      <div className="income-preview-container">
        <div className="preview-paper">
          {/* ✅ WEHAGO 원본에는 로고가 없음 — 제목이 최상단 */}
          <div className="er-title">{L.docTitle}({categoryLabel || "-"})</div>

          {/* ✅ 우상단 결재란 — 결재자 1인당 칸 하나, 2단 구성(상단 헤더: "직위 이름",
              항상 표시 / 하단 본문: 미결재면 완전 빈칸, 완료면 원형 배지("합의"/"승인")
              + 완료일자만 — 이름은 절대 하단에 넣지 않는다). 결재 상태는 ApprovalLine의
              result/approved_at 그대로 사용(신규 작성 단계에서는 결과가 없어 항상
              빈 칸으로 보임 — 저장 후 실제 결재가 진행되면 상세 화면에서 채워짐) */}
          <div className="er-approval-wrap">
            <div className="er-approval-grid">
              {approvalLine.map((a, i) => {
                const isDone = a.result === "approved";
                // 생략 칸은 원형 배지를 쓰지 않는다 — 인쇄물에서 승인 도장처럼 보이면 안 된다.
                const isSkipped = a.result === "skipped";
                const badgeText = a.approvalType === "합의" ? "합의" : "승인";
                const dateText = a.approved_at ? String(a.approved_at).slice(0, 10).replace(/-/g, ".") : "";
                return (
                  <div className="er-approval-box" key={a.id ?? i}>
                    <div className="er-approval-header">
                      {[a.position, a.name].filter(Boolean).join(" ") || "-"}
                    </div>
                    <div className="er-approval-body">
                      {isDone && (
                        <>
                          <div className="er-approval-badge">{badgeText}</div>
                          <div className="er-approval-date">{dateText}</div>
                        </>
                      )}
                      {isSkipped && (
                        <>
                          <div style={{ color: "#7f8c8d", fontWeight: 700, whiteSpace: "nowrap" }}>생략</div>
                          <div className="er-approval-date">{dateText}</div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ✅ 필드-값 2열 테이블 — 한 행 = 라벨 1 + 값 1(원본 순서 그대로 세로 나열,
              한 행에 필드 2개씩 압축하던 이전 레이아웃 전면 해체) */}
          <table className="er-field-table">
            <tbody>
              <tr>
                <th>문서제목</th>
                <td>{documentTitle || "-"}</td>
              </tr>
              <tr>
                <th>작성일자</th>
                <td>{draftDate || "-"}</td>
              </tr>
              <tr>
                <th>기안자</th>
                <td>
                  {drafterLine1 || "-"}
                  {drafterLine2 && (
                    <>
                      <br />
                      {drafterLine2}
                    </>
                  )}
                </td>
              </tr>
              <tr>
                <th>{L.dateLabel}</th>
                <td>{transactionDate || "-"}</td>
              </tr>
              <tr>
                <th>거래처</th>
                <td>{partnerName || "-"}</td>
              </tr>
              <tr>
                <th>{L.amountLabel}</th>
                <td>{amount ? `${Number(amount).toLocaleString()}원` : "-"}</td>
              </tr>
              <tr>
                <th>{L.methodLabel}</th>
                <td>{methodLabel || "-"}</td>
              </tr>
              <tr>
                <th>{L.accountLabel}</th>
                <td>{accountInfo || "-"}</td>
              </tr>
              <tr>
                <th>{L.budgetLabel}({categoryLabel || "-"})</th>
                <td>{budgetItemFormatted || "-"}</td>
              </tr>
              <tr>
                <th>내부기안번호</th>
                <td>{internalDraftNo || "-"}</td>
              </tr>
              <tr>
                <th>첨부</th>
                <td>{attachmentsText || "-"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
